import { Component, OnDestroy, OnInit } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { PermissionName } from '@src/app/model/userdata';
import { BehaviorSubject, firstValueFrom, Observable, Subject, takeUntil } from 'rxjs';
import { PermissionsService } from '@src/app/services/permissions/permissions.service';
import { ExcelService } from '@src/app/services/excel/excel.service';
import { ScopeService } from '@src/app/services/scope/scope.service';
import { ApolloService } from '@src/app/services/apollo/apollo.service';
import { map, take } from 'rxjs/operators';
import { ItemPriceStock, WoodPriceStock } from '@src/app/model/price-stock-data';
import { RetailerXLSXData } from '@src/app/model/RetailerXLSXData';
import { PriceStock } from '@src/app/model/price-stock';

@Component({
  selector: 'app-retailer-data',
  templateUrl: './retailer-data.component.html',
  styleUrls: ['./retailer-data.component.scss'],
  standalone: false,
})
export class RetailerDataComponent implements OnInit, OnDestroy {
  public hasEditRetailerDataPermission$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  private _componentDestroyed$: Subject<void> = new Subject();
  private _currentRetailerStock$: Observable<PriceStock[]>;

  constructor(
    private _apolloService: ApolloService,
    private _translateService: TranslateService,
    private _permissionsService: PermissionsService,
    private _excelService: ExcelService,
    private _scopeService: ScopeService
  ) {
  }

  public ngOnInit(): void {
    this._permissionsService.observePermission$('access_retailer_data', PermissionName.Update)
      .pipe(takeUntil(this._componentDestroyed$))
      .subscribe(this.hasEditRetailerDataPermission$);

    this._currentRetailerStock$ = this._apolloService.getCurrentRetailer$()
      .pipe(takeUntil(this._componentDestroyed$))
      .pipe(map((r) => {
        if (r) {
          return r.stock;
        } else {
          window.alert(this._translateService.instant('retailerData.excelUpload.dataNotFound'));
        }
      }));
  }

  public ngOnDestroy(): void {
    this._componentDestroyed$.next();
    this._componentDestroyed$.complete();
  }

  /**
   * Download an Excel file with the current retailer's price stock data
   */
  public downloadRetailerData(): void {
    this._currentRetailerStock$.pipe(take(1)).subscribe((stock) => {
      if (stock) {
        const priceStockData = stock.reduce((priceStockList: RetailerXLSXData[], current: PriceStock) => {
          if (current.data instanceof ItemPriceStock) {
            priceStockList.push(
              new RetailerXLSXData(
                current.data.EAN,
                current.data.price,
                current.data.internalCode,
                current.data.purchasePrice,
                current.data.discountPrice
              )
            );
          } else if (current.data instanceof WoodPriceStock) {
            current.data.prices.forEach((p) => {
              priceStockList.push(
                new RetailerXLSXData(p.EAN, p.price, p.internalCode, p.purchasePrice, p.discountPrice)
              );
            });
          }
          return priceStockList;
        }, [])
          .filter((value, index, self) => // Filter on unique EANS
            self.findIndex(x => x['EAN'] === value['EAN']) === index
          );

        this._excelService.generateXlsx(priceStockData);
      }
    });
  }

  /**
   * Given a file event as input, try to upload it.
   */
  public async uploadRetailerData(event: any): Promise<void> {
    const files = event.target.files;
    try {
      const scope = this._scopeService.currentScope$.getValue()?.id;
      if (files.length > 0 && scope) {
        const stock = await firstValueFrom(this._currentRetailerStock$)
          .catch(
            (e) => {
              throw e instanceof Error ? e : Error(e);
            }
          );
        const data: RetailerXLSXData[] = await this._excelService.readRetailerXlsx$(files[0])
          .catch(
            (e) => {
              throw e instanceof Error ? e : Error(e);
            }
          );
        await firstValueFrom(this._excelService.doPriceStockMutationFromXLSXData$(data, stock, scope.toString()))
          .catch(
            (e) => {
              throw e instanceof Error ? e : Error(e);
            }
          );
        this._apolloService.getCurrentRetailerRefresh();
        window.alert(this._translateService.instant('retailerData.excelUpload.success'));
      } else {
        throw Error(this._translateService.instant('retailerData.excelUpload.noFile'));
      }
    } catch (e) {
      window.alert(e instanceof Error ? e.message : e);
    }
  }
}
